import {
  A2uiSurfaceStatus,
  Message,
  MessageA2uiSurface,
  MessageRole,
} from '../types/chat';
import {
  resolveMessageA2uiApproval,
  upsertMessageA2uiSurface,
} from './parse-message-a2ui';

export function takeApprovalSurfaces(surfaces?: MessageA2uiSurface[]): {
  remaining?: MessageA2uiSurface[];
  approvals: MessageA2uiSurface[];
} {
  const approvals: MessageA2uiSurface[] = [];
  const remaining: MessageA2uiSurface[] = [];
  for (const surface of surfaces ?? []) {
    if (surface.approval) {
      approvals.push(surface);
    } else {
      remaining.push(surface);
    }
  }
  return {
    remaining: remaining.length ? remaining : undefined,
    approvals,
  };
}

export function createApprovalConversationMessage(
  surface: MessageA2uiSurface,
  refs: { userMessageId?: string; assistantMessageId: string }
): Message {
  const recordId = surface.approval?.approvalId || surface.surfaceId;
  return {
    role: MessageRole.APPROVAL,
    recordId,
    ...(refs.userMessageId && { userRecordId: refs.userMessageId }),
    activeGenerationIndex: 0,
    generations: [
      {
        id: recordId,
        content: '',
        createdDate: Date.now(),
        a2uiSurfaces: [surface],
        ...(refs.userMessageId && { parentUserGenerationId: refs.userMessageId }),
        parentIds: [refs.assistantMessageId],
      },
    ],
  };
}

function approvalMessageMatches(message: Message, surface: MessageA2uiSurface): boolean {
  if (message.role !== MessageRole.APPROVAL) return false;
  const approvalId = surface.approval?.approvalId;
  return message.generations.some((generation) =>
    generation.a2uiSurfaces?.some(
      (entry) =>
        entry.surfaceId === surface.surfaceId ||
        (approvalId != null && entry.approval?.approvalId === approvalId)
    )
  );
}

/** Append or update an approval as its own conversation message (socket order). */
export function upsertConversationApproval(
  messages: Message[],
  surface: MessageA2uiSurface,
  refs: { userMessageId?: string; assistantMessageId: string }
): Message[] {
  const index = messages.findIndex((message) => approvalMessageMatches(message, surface));
  if (index === -1) {
    return [...messages, createApprovalConversationMessage(surface, refs)];
  }

  return messages.map((message, msgIndex) => {
    if (msgIndex !== index) return message;
    return {
      ...message,
      generations: message.generations.map((generation, genIndex) =>
        genIndex === (message.activeGenerationIndex ?? 0)
          ? {
              ...generation,
              a2uiSurfaces: upsertMessageA2uiSurface(generation.a2uiSurfaces, surface, 'surface'),
            }
          : generation
      ),
    };
  });
}

export function resolveConversationApproval(
  messages: Message[],
  resolved: { approvalId: string; decision: 'approved' | 'declined'; resolvedBy?: string }
): Message[] {
  return messages.map((message) => {
    if (message.role !== MessageRole.APPROVAL) return message;
    return {
      ...message,
      generations: message.generations.map((generation) =>
        generation.a2uiSurfaces?.some((surface) => surface.approval?.approvalId === resolved.approvalId)
          ? {
              ...generation,
              a2uiSurfaces: resolveMessageA2uiApproval(generation.a2uiSurfaces, resolved),
            }
          : generation
      ),
    };
  });
}

/** History: lift approval surfaces off assistant generations into following messages. */
export function promoteApprovalSurfacesToMessages(messages: Message[]): Message[] {
  const result: Message[] = [];

  for (const message of messages) {
    if (message.role !== MessageRole.ASSISTANT && message.role !== MessageRole.ASSISTANT_CHAT) {
      result.push(message);
      continue;
    }

    const nextGenerations = message.generations.map((generation) => {
      const { remaining, approvals } = takeApprovalSurfaces(generation.a2uiSurfaces);
      return { generation: { ...generation, a2uiSurfaces: remaining }, approvals };
    });

    result.push({
      ...message,
      generations: nextGenerations.map((entry) => entry.generation),
    });

    for (const entry of nextGenerations) {
      const assistantMessageId = entry.generation.id || message.recordId;
      const userMessageId = message.userRecordId ?? entry.generation.parentUserGenerationId;
      for (const surface of entry.approvals) {
        result.push(
          createApprovalConversationMessage(surface, { userMessageId, assistantMessageId })
        );
      }
    }
  }

  return result;
}

function expireSurfaceIfActive(surface: MessageA2uiSurface): MessageA2uiSurface {
  if (!surface.approval || surface.status !== 'active') return surface;
  return { ...surface, status: 'expired' };
}

/** Mark live pending HIL cards expired when the user starts a new turn (matches BE ledger). */
export function expireActiveApprovals(messages: Message[]): Message[] {
  let changed = false;

  const next = messages.map((message) => {
    let messageChanged = false;
    const generations = message.generations.map((generation) => {
      const hasActiveSurface = generation.a2uiSurfaces?.some(
        (surface) => surface.approval && surface.status === 'active'
      );
      const hasActiveBlock = generation.streamBlocks?.some(
        (block) =>
          (block.type === 'approval' || block.type === 'a2ui') &&
          block.surface.approval &&
          block.surface.status === 'active'
      );
      if (!hasActiveSurface && !hasActiveBlock) return generation;

      messageChanged = true;
      changed = true;
      return {
        ...generation,
        a2uiSurfaces: generation.a2uiSurfaces?.map(expireSurfaceIfActive),
        streamBlocks: generation.streamBlocks?.map((block) => {
          if (block.type !== 'approval' && block.type !== 'a2ui') return block;
          return { ...block, surface: expireSurfaceIfActive(block.surface) };
        }),
      };
    });

    return messageChanged ? { ...message, generations } : message;
  });

  return changed ? next : messages;
}

/** USER and USER_CHAT share wire value `'user'`; compare by value, not enum member. */
export function isUserMessageRole(role: MessageRole): boolean {
  return String(role) === MessageRole.USER;
}

/** True when no later user turn has superseded this HIL interrupt (regen does not). */
export function isLatestApprovalTurn(messages: Message[], messageIndex: number): boolean {
  return !messages.slice(messageIndex + 1).some((later) => isUserMessageRole(later.role));
}

/** Display status for a live approval card. Only `expiresAt` makes it Expired. */
export function resolveApprovalCardStatus(
  surface: MessageA2uiSurface,
  now = Date.now()
): A2uiSurfaceStatus {
  if (surface.status !== 'active') return surface.status;
  const expiresAtMs = surface.approval?.expiresAt
    ? Date.parse(surface.approval.expiresAt)
    : Number.NaN;
  if (!Number.isNaN(expiresAtMs) && expiresAtMs <= now) return 'expired';
  return 'active';
}

export function conversationTurnHasPendingApproval(
  messages: Message[],
  userRecordId?: string
): boolean {
  const now = Date.now();
  return messages.some((message) => {
    if (message.role !== MessageRole.APPROVAL) return false;
    if (userRecordId && message.userRecordId && message.userRecordId !== userRecordId) {
      return false;
    }
    return message.generations.some((generation) =>
      generation.a2uiSurfaces?.some((surface) => {
        if (!surface.approval || surface.status !== 'active') return false;
        const expiry = surface.approval.expiresAt
          ? Date.parse(surface.approval.expiresAt)
          : Number.NaN;
        return Number.isNaN(expiry) || expiry > now;
      })
    );
  });
}
